'use client'

import React, { useEffect, useState } from 'react'
import { VariantProps } from 'class-variance-authority'

import { cn } from '../../utils/cn'
import { inputColor, inputVariants } from '../input'

export interface InputProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof inputVariants> {
  /** Accessible label that appears above the input (required if no aria-label prop is present) */
  label?: string
  /** Displays this message below the input in gray (if there is no errorMessage) */
  hint?: string
  /** sets input as invalid */
  invalid?: boolean
  /** Displays this message in red below the input */
  errorMessage?: string
  color?: 'white' | 'gray'
}

const Textarea = React.forwardRef<HTMLTextAreaElement, InputProps>(
  (
    {
      className,
      label,
      hint,
      invalid,
      errorMessage,
      color,
      variantSize,
      ...props
    },
    ref
  ) => {
    const [uid] = useState<string>(React.useId())

    useEffect(() => {
      if (!props['aria-label'] && !label) {
        throw new Error(
          'Input elements require either a label or an aria-label for accessibility.'
        )
      }
    }, [])

    const renderDescription = () => {
      if (errorMessage) {
        return (
          <span
            role="alert"
            id={`message-${uid}`}
            className="text-error-700 text-xs"
          >
            {errorMessage}
          </span>
        )
      }
      if (hint) {
        return (
          <span id={`message-${uid}`} className="text-xs text-neutral-500">
            {hint}
          </span>
        )
      } else {
        return (
          <span role="presentation" style={{ visibility: 'hidden' }}>
            ...
          </span>
        )
      }
    }

    return (
      <div>
        {label && (
          <label
            htmlFor={`input-${uid}`}
            className={cn(invalid && 'text-error-700', 'font-medium')}
          >
            {label}
          </label>
        )}
        <textarea
          id={`input-${uid}`}
          aria-invalid={invalid}
          aria-describedby={`message-${uid}`}
          className={cn(
            invalid ? 'border-error-500' : 'border-neutral-500',
            inputVariants({ variantSize }),
            className,
            inputColor(color, invalid)
          )}
          ref={ref}
          {...props}
        />
        {renderDescription()}
      </div>
    )
  }
)
Textarea.displayName = 'Textarea'

export { Textarea }
